import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { fetchAndApplyDenominations } from './utils/helpers';
import { isRefreshTokenValid } from './services/auth';

// Layout Components
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';

// Page Components
import Login from './pages/Login';
import OrderRequest from './pages/OrderRequest';
import PendingOrders from './pages/PendingOrders';
import AllOrders from './pages/AllOrders';
import AccountInfo from './pages/AccountInfo';
import Help from './pages/Help';
import ManagementClients from './pages/ManagementClients';
import ManagementPendingOrders from './pages/ManagementPendingOrders';
import ManagementAllOrders from './pages/ManagementAllOrders';
import ManagementDiscounts from './pages/ManagementDiscounts';
import MoneyDeposit from './pages/MoneyDeposit';
import PreparingOrders from './pages/PreparingOrders';
import ManagementScratchOrders from './pages/ManagementScratchOrders';
import ManagementOverview from './pages/ManagementOverview';
import EmployeeScratchOrders from './pages/EmployeeScratchOrders';
import SupplierPendingOrders from './pages/SupplierPendingOrders';
import SupplierOrderHistory from './pages/SupplierOrderHistory';

const ProtectedRoute = ({ children }) => {
    const { isAuth, initializing, logout } = useAuth();

    useEffect(() => {
        if (!initializing && isAuth) {
            const valid = isRefreshTokenValid();
            if (!valid) {
                logout();
            }
        }
    }, [initializing, isAuth, logout]);

    if (initializing) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Validating session...</p>
                </div>
            </div>
        );
    }

    if (!isAuth) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

const DealerEmployeeRoute = ({ children }) => {
    const { user } = useAuth();
    if (user?.role === 'KTE-management' || user?.accountType === 'KTE-management') {
        return <Navigate to="/management/overview" replace />;
    }
    return children;
};

const ManagementRoute = ({ children }) => {
    const { user } = useAuth();
    if (!(user?.role === 'KTE-management' || user?.accountType === 'KTE-management')) {
        return <Navigate to="/order-request" replace />;
    }
    return children;
};

const SupplierRoute = ({ children }) => {
    const { user } = useAuth();
    if (!(user?.role === 'Supplier' || user?.accountType === 'Supplier')) {
        return <Navigate to="/order-request" replace />;
    }
    return children;
};

const KTESalesRoute = ({ children }) => {
    const { user } = useAuth();
    if (!(user?.role === 'KTE-sales' || user?.accountType === 'KTE-sales' || user?.role === 'KTE-management' || user?.accountType === 'KTE-management')) {
        return <Navigate to="/order-request" replace />;
    }
    return children;
};

const KTEEmployeeRoute = ({ children }) => {
    const { user } = useAuth();
    if (!(user?.role === 'KTE-Employees' || user?.accountType === 'KTE-Employees')) {
        return <Navigate to="/order-request" replace />;
    }
    return children;
};

const AuthenticatedLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
                    {children}
                </main>
            </div>
        </div>
    );
};

function App() {
    const { user, isAuth, initializing } = useAuth();

    useEffect(() => {
        fetchAndApplyDenominations();
    }, []);

    const getDefaultRoute = () => {
        if (user?.role === 'KTE-management' || user?.accountType === 'KTE-management') {
            return '/management/pending-orders';
        }
        if (user?.role === 'KTE-sales' || user?.accountType === 'KTE-sales') {
            return '/management/discounts';
        }
        if (user?.role === 'Supplier' || user?.accountType === 'Supplier') {
            return '/supplier/pending-orders';
        }
        if (user?.role === 'KTE-Employees' || user?.accountType === 'KTE-Employees') {
            return '/employee/scratch-orders-management';
        }
        return '/order-request';
    };

    if (initializing) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading application...</p>
                </div>
            </div>
        );
    }

    return (
        <Router>
            <div className="App">
                <Routes>
                    <Route
                        path="/login"
                        element={
                            isAuth ? <Navigate to={getDefaultRoute()} replace /> : <Login />
                        }
                    />

                    {/* Management Routes */}
                    <Route path="/management/overview" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><ManagementOverview /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/pending-orders" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><ManagementPendingOrders /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/clients" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><ManagementClients /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/all-orders" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><ManagementAllOrders /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/money-deposit" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><MoneyDeposit /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/preparing-orders" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><PreparingOrders /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/scratch-orders-management" element={<ProtectedRoute><ManagementRoute><AuthenticatedLayout><ManagementScratchOrders /></AuthenticatedLayout></ManagementRoute></ProtectedRoute>} />
                    <Route path="/management/discounts" element={<ProtectedRoute><KTESalesRoute><AuthenticatedLayout><ManagementDiscounts /></AuthenticatedLayout></KTESalesRoute></ProtectedRoute>} />

                    {/* Employee Routes */}
                    <Route path="/employee/scratch-orders-management" element={<ProtectedRoute><KTEEmployeeRoute><AuthenticatedLayout><EmployeeScratchOrders /></AuthenticatedLayout></KTEEmployeeRoute></ProtectedRoute>} />

                    {/* Dealer/Employee Only Routes */}
                    <Route path="/order-request" element={<ProtectedRoute><DealerEmployeeRoute><AuthenticatedLayout><OrderRequest /></AuthenticatedLayout></DealerEmployeeRoute></ProtectedRoute>} />
                    <Route path="/pending-orders" element={<ProtectedRoute><DealerEmployeeRoute><AuthenticatedLayout><PendingOrders /></AuthenticatedLayout></DealerEmployeeRoute></ProtectedRoute>} />
                    <Route path="/all-orders" element={<ProtectedRoute><DealerEmployeeRoute><AuthenticatedLayout><AllOrders /></AuthenticatedLayout></DealerEmployeeRoute></ProtectedRoute>} />
                    <Route path="/account-info" element={<ProtectedRoute><DealerEmployeeRoute><AuthenticatedLayout><AccountInfo /></AuthenticatedLayout></DealerEmployeeRoute></ProtectedRoute>} />
                    <Route path="/help" element={<ProtectedRoute><DealerEmployeeRoute><AuthenticatedLayout><Help /></AuthenticatedLayout></DealerEmployeeRoute></ProtectedRoute>} />

                    {/* Supplier Only Routes */}
                    <Route path="/supplier/pending-orders" element={<ProtectedRoute><SupplierRoute><AuthenticatedLayout><SupplierPendingOrders /></AuthenticatedLayout></SupplierRoute></ProtectedRoute>} />
                    <Route path="/supplier/order-history" element={<ProtectedRoute><SupplierRoute><AuthenticatedLayout><SupplierOrderHistory /></AuthenticatedLayout></SupplierRoute></ProtectedRoute>} />

                    <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
                    <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
                </Routes>
            </div>
        </Router>
    );
}

export default App;
